import React, { useState, useEffect } from 'react';
import {
    Typography,
    useMediaQuery,
    useTheme,
    Divider,
    Chip,
    Fade,
    Avatar,
    CircularProgress,
    Box
} from '@mui/material';
import {
    Person,
    Bookmark,
    Shield
} from '@mui/icons-material';
import HeaderTopSection from "../components/partials/HeaderTop";
import InformationPersonnelle from "../components/profile/InformationPersonnelle";
import Securite from "../components/profile/Securite";
import UserService from "../services/UserService";
import AuthService from "../services/AuthService";
import '../css/profile.css';
import Banner from "../components/partials/Banner";

// Dictionnaire de traduction pour les rôles prédéfinis
const ROLE_TRANSLATIONS = {
    'SuperAdmin': 'Super Administrateur',
    'PROFESSIONAL': 'Professionnel',
    'CLIENT': 'Client',
    'USER': 'Utilisateur',
    'Admin': 'Administrateur'
};

// Fonction pour obtenir le libellé du rôle
const getRoleLabel = (roleName) => {
    return ROLE_TRANSLATIONS[roleName] || roleName;
};

const ProfilePage = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeMenuIndex, setActiveMenuIndex] = useState(0);

    // Configuration des menus - toujours 2 menus
    const menuItems = [
        {
            icon: Person,
            label: 'Information Personnelle',
            active: activeMenuIndex === 0,
        },
        {
            icon: Bookmark,
            label: 'Sécurité',
            active: activeMenuIndex === 1,
        }
    ];

    // Récupérer les données utilisateur au chargement
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                setLoading(true);
                setError(null);

                const currentUser = AuthService.getCurrentUser();

                if (!currentUser || !currentUser.id) {
                    throw new Error('Utilisateur non connecté');
                }

                const userResponse = await UserService.getUserById(currentUser.id);
                setUserData(userResponse);

            } catch (error) {
                console.error('Erreur lors de la récupération des données utilisateur:', error);
                setError('Impossible de charger les données utilisateur');
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, []);

    const handleMenuClick = (index) => {
        setActiveMenuIndex(index);
    };

    const renderSidebar = () => {
        if (loading) {
            return (
                <div className="profile-sidebar">
                    <Fade in timeout={800}>
                        <div className="profile-card">
                            <div className="profile-card-content">
                                <div className="profile-avatar-section">
                                    <div className="profile-avatar-container">
                                        <CircularProgress size={60} />
                                    </div>
                                    <Typography variant="h5" className="profile-name">
                                        Chargement...
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        @utilisateur
                                    </Typography>
                                </div>
                                <Divider className="profile-divider" />
                                {/* Menu Navigation */}
                                <div className="profile-menu">
                                    {menuItems.map((item, index) => {
                                        const IconComponent = item.icon;
                                        return (
                                            <button
                                                key={index}
                                                className={`profile-menu-btn ${item.active ? 'active' : ''}`}
                                                onClick={() => handleMenuClick(index)}
                                            >
                                                <IconComponent />
                                                {item.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </Fade>
                </div>
            );
        }

        if (error) {
            return (
                <div className="profile-sidebar">
                    <Fade in timeout={800}>
                        <div className="profile-card">
                            <div className="profile-card-content">
                                <div className="profile-avatar-section">
                                    <div className="profile-avatar-container">
                                        <Avatar
                                            className="profile-avatar"
                                            alt="Erreur"
                                        />
                                    </div>
                                    <Typography variant="h5" className="profile-name">
                                        Erreur
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        @utilisateur
                                    </Typography>
                                </div>
                                <Divider className="profile-divider" />
                                {/* Menu Navigation */}
                                <div className="profile-menu">
                                    {menuItems.map((item, index) => {
                                        const IconComponent = item.icon;
                                        return (
                                            <button
                                                key={index}
                                                className={`profile-menu-btn ${item.active ? 'active' : ''}`}
                                                onClick={() => handleMenuClick(index)}
                                            >
                                                <IconComponent />
                                                {item.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </Fade>
                </div>
            );
        }

        // Construire le nom complet
        const fullName = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || 'Utilisateur';

        // Utiliser l'image de profil de l'utilisateur ou une image par défaut
        const profileImage = userData?.profilePictureUrl || "/assets/images/profile/profile-default.png";

        // Utiliser le username ou "utilisateur" par défaut
        const username = userData?.username || 'utilisateur';

        // Extraire les rôles ou retourner un tableau vide
        const userRoles = userData?.roles || [];

        return (
            <div className="profile-sidebar">
                <Fade in timeout={800}>
                    <div className="profile-card">
                        <div className="profile-card-content">
                            {/* Section Avatar */}
                            <div className="profile-avatar-section">
                                <div className="profile-avatar-container">
                                    <Avatar
                                        src="/assets/images/profile/profile-default.png"
                                        className="profile-avatar"
                                        alt={fullName}
                                        sx={{
                                            width: 100,
                                            height: 100,
                                            border: '4px solid',
                                            borderColor: 'primary.main'
                                        }}
                                    />
                                </div>

                                <Typography variant="h5" className="profile-name" sx={{ mt: 2 }}>
                                    {fullName}
                                </Typography>

                                {/* Nom d'utilisateur affiché en texte simple */}
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    @{username}
                                </Typography>

                                {/* Affichage des rôles */}
                                {userRoles.length > 0 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 1,
                                            justifyContent: 'center',
                                            mt: 1
                                        }}
                                    >
                                        {userRoles.map((role) => (
                                            <Chip
                                                key={role.id}
                                                icon={<Shield style={{ fontSize: 16 }} />}
                                                label={getRoleLabel(role.name)}
                                                color="primary"
                                                variant="outlined"
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                )}
                            </div>

                            <Divider className="profile-divider" sx={{ mt: 2 }} />

                            {/* Menu Navigation */}
                            <div className="profile-menu">
                                {menuItems.map((item, index) => {
                                    const IconComponent = item.icon;
                                    return (
                                        <button
                                            key={index}
                                            className={`profile-menu-btn ${item.active ? 'active' : ''}`}
                                            onClick={() => handleMenuClick(index)}
                                        >
                                            <IconComponent />
                                            {item.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </Fade>
            </div>
        );
    };

    const renderContent = () => {
        switch(activeMenuIndex) {
            case 0:
                return <InformationPersonnelle />;
            case 1:
                return <Securite />;
            default:
                return <InformationPersonnelle />;
        }
    };

    return (
        <>
            <HeaderTopSection />
            <Banner
                title="Mon Profil"
            />

            <div className="profile-container">
                <div className="profile-content">
                    <div className="profile-grid">
                        {renderSidebar()}
                        {renderContent()}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProfilePage;